import React, { useState, useEffect, useCallback } from 'react';
import { getApiBaseUrl } from '../../services/api';
import { useTranslation } from '../../context/LanguageContext';

const DASH = '—';

/* 플랫폼 > 시스템 현황 — GET /platform/status 가 주는 값만 보여 준다.
 * 예전에는 응답이 없으면 코드에 적어 둔 서비스 5개·가동률·ETL 시각·DB 크기를 대신 보여 줬다(실측처럼 보임).
 * 서버는 서비스별 가동률을 주지 않으므로 그 열은 뺐다. 값이 없으면 — */
function SystemStatusPage() {
    const { t } = useTranslation();
    const [status, setStatus] = useState(null);
    const [loaded, setLoaded] = useState(false);

    const fetchStatus = useCallback(async () => {
        try {
            const token = localStorage.getItem('token') || sessionStorage.getItem('token');
            const response = await fetch(`${getApiBaseUrl()}/platform/status`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            const result = await response.json();
            if (result.success) {
                setStatus(result.data);
            }
        } catch (error) {
            console.error('시스템 상태 조회 실패:', error);
        } finally {
            setLoaded(true);
        }
    }, []);

    useEffect(() => {
        fetchStatus();
        const interval = setInterval(fetchStatus, 30000);
        return () => clearInterval(interval);
    }, [fetchStatus]);

    const services = Array.isArray(status?.services) ? status.services : [];
    const etl = status?.etl || {};
    const database = status?.database || {};
    const show = (v) => (v == null || v === '' || v === '-' ? DASH : v); // 서버는 조회 실패 시 '-' 를 준다
    const count = (v) => (v == null ? DASH : Number(v).toLocaleString());

    const serviceStatusLabel = (s) => {
        if (s === 'running') return t('pages.platform.systemStatus.statusRunning');
        if (s === 'error') return t('pages.platform.systemStatus.statusError');
        return show(s);
    };

    const localizeEtlStatus = (s) => {
        if (s === 'SUCCESS') return t('pages.platform.systemStatus.etl.success');
        if (s === 'NO_DATA') return t('pages.platform.systemStatus.etl.noData');
        if (s === 'ERROR') return t('pages.platform.systemStatus.etl.error');
        return s;
    };

    const stats = [
        { icon: 'business', value: status?.companies?.total, label: 'totalCompanies' },
        { icon: 'check_circle', value: status?.companies?.active, label: 'activeCompanies' },
        { icon: 'group', value: status?.users?.total, label: 'totalUsers' },
        { icon: 'person_check', value: status?.users?.active, label: 'activeUsers' }
    ];

    return (
        <div className="platform-page">
            {/* 전체 통계 */}
            <div className="platform-stats-grid">
                {stats.map((s) => (
                    <div key={s.label} className="platform-stat-card">
                        <div className="platform-stat-icon">
                            <span className="material-symbols-outlined">{s.icon}</span>
                        </div>
                        <div className="platform-stat-info">
                            <div className="platform-stat-value">{count(s.value)}</div>
                            <div className="platform-stat-label">{t(`pages.platform.systemStatus.stats.${s.label}`)}</div>
                        </div>
                    </div>
                ))}
            </div>

            {/* 서비스 상태 */}
            <div className="platform-table-container">
                <div className="platform-table-header">
                    <h3 className="platform-table-title">{t('pages.platform.systemStatus.serviceStatus')}</h3>
                    <button type="button" className="platform-btn platform-btn-secondary" onClick={fetchStatus}>
                        <span className="material-symbols-outlined">refresh</span>
                        {t('pages.platform.systemStatus.refresh')}
                    </button>
                </div>
                {services.length === 0 ? (
                    <div className="platform-empty">
                        <span className="material-symbols-outlined platform-empty-icon">dns</span>
                        <div className="platform-empty-text">{loaded ? t('pages.platform.systemStatus.noStatus') : t('pages.platform.companies.loading')}</div>
                    </div>
                ) : (
                    <div className="platform-table-scroll">
                        <table className="platform-table">
                            <thead>
                                <tr>
                                    <th>{t('pages.platform.systemStatus.col.service')}</th>
                                    <th>{t('pages.platform.systemStatus.col.status')}</th>
                                    <th>{t('pages.platform.systemStatus.col.url')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {services.map((service) => (
                                    <tr key={service.name}>
                                        <td><strong>{service.name}</strong></td>
                                        <td>
                                            <span className="platform-status">
                                                <span className={`platform-dot ${service.status || ''}`} />
                                                {serviceStatusLabel(service.status)}
                                            </span>
                                        </td>
                                        <td><span className="platform-code">{show(service.url)}</span></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* ETL 상태 & DB 상태 */}
            <div className="platform-two-col">
                <div className="platform-table-container">
                    <div className="platform-table-header">
                        <h3 className="platform-table-title">{t('pages.platform.systemStatus.etlTitle')}</h3>
                    </div>
                    <div className="platform-kv">
                        <div className="platform-kv-row">
                            <span className="platform-kv-label">{t('pages.platform.systemStatus.etl.lastRun')}</span>
                            <span className="platform-kv-value">{show(etl.lastRun)}</span>
                        </div>
                        <div className="platform-kv-row">
                            <span className="platform-kv-label">{t('pages.platform.systemStatus.etl.status')}</span>
                            {etl.status ? (
                                <span className={`platform-badge ${etl.status === 'SUCCESS' ? 'active' : etl.status === 'NO_DATA' ? '' : 'inactive'}`}>
                                    {localizeEtlStatus(etl.status)}
                                </span>
                            ) : <span className="platform-kv-value">{DASH}</span>}
                        </div>
                        <div className="platform-kv-row">
                            <span className="platform-kv-label">{t('pages.platform.systemStatus.etl.records')}</span>
                            <span className="platform-kv-value">
                                {etl.recordsProcessed == null ? DASH : t('pages.platform.systemStatus.etl.recordsSuffix', { n: Number(etl.recordsProcessed).toLocaleString() })}
                            </span>
                        </div>
                        <div className="platform-kv-row">
                            <span className="platform-kv-label">{t('pages.platform.systemStatus.etl.sync')}</span>
                            <span className="platform-kv-value">{show(etl.syncInterval)}</span>
                        </div>
                    </div>
                </div>

                <div className="platform-table-container">
                    <div className="platform-table-header">
                        <h3 className="platform-table-title">{t('pages.platform.systemStatus.dbTitle')}</h3>
                    </div>
                    <div className="platform-kv">
                        <div className="platform-kv-row">
                            <span className="platform-kv-label">{t('pages.platform.systemStatus.db.tables')}</span>
                            <span className="platform-kv-value">
                                {database.totalTables == null ? DASH : t('pages.platform.systemStatus.db.tablesSuffix', { n: database.totalTables })}
                            </span>
                        </div>
                        <div className="platform-kv-row">
                            <span className="platform-kv-label">{t('pages.platform.systemStatus.db.records')}</span>
                            <span className="platform-kv-value">{show(database.totalRecords)}</span>
                        </div>
                        <div className="platform-kv-row">
                            <span className="platform-kv-label">{t('pages.platform.systemStatus.db.size')}</span>
                            <span className="platform-kv-value">{show(database.dbSize)}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default SystemStatusPage;
